import { useEffect, useRef } from 'react';
import type { Map as MapLibreMap } from 'maplibre-gl';
import { useWeatherStore } from '@/stores/weatherStore';
import { WindRenderer } from '@/lib/weather/WindRenderer';

/**
 * Hook that manages the canvas-based wind particle animation.
 *
 * Wind data is fetched as a global grid (covers -180..180 lon, -70..70 lat)
 * so there's no need to refetch on pan/zoom. The data is cached for 15 min.
 */
export function useWindLayer(map: MapLibreMap | null) {
  const { windEnabled, windOpacity, windData, fetchWindData } = useWeatherStore();
  const rendererRef = useRef<WindRenderer | null>(null);
  const fetchedRef = useRef(false);

  // Create/destroy renderer when map is available
  useEffect(() => {
    if (!map) return;

    const renderer = new WindRenderer(map, {
      particleCount: 4000,
      fadeOpacity: 0.96,
      lineWidth: 1.2,
      speedFactor: 0.3,
      particleMaxAge: 200,
    });
    rendererRef.current = renderer;

    return () => {
      renderer.destroy();
      rendererRef.current = null;
    };
  }, [map]);

  // Start/stop renderer based on windEnabled and windData
  useEffect(() => {
    const renderer = rendererRef.current;
    if (!renderer) return;

    if (windEnabled && windData) {
      renderer.setWindData(windData);
      renderer.start();
    } else {
      renderer.stop();
      renderer.setWindData(null);
    }
  }, [windEnabled, windData]);

  // Update opacity
  useEffect(() => {
    rendererRef.current?.setOpacity(windOpacity);
  }, [windOpacity]);

  // Fetch global wind data once when enabled
  useEffect(() => {
    if (!windEnabled) {
      fetchedRef.current = false;
      return;
    }
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    // Bounds are ignored by the global fetch, but the API signature requires them
    fetchWindData({ west: -180, east: 180, south: -70, north: 70 });
  }, [windEnabled, fetchWindData]);

  // Listen for wind forecast time changes
  useEffect(() => {
    if (!windEnabled) return;

    const handleWindTimeChange = () => {
      fetchedRef.current = false; // force refetch with new hour
      fetchWindData({ west: -180, east: 180, south: -70, north: 70 });
    };

    window.addEventListener('wind-time-change', handleWindTimeChange);
    return () => window.removeEventListener('wind-time-change', handleWindTimeChange);
  }, [windEnabled, fetchWindData]);
}
